// FAQ copy from the v2 page, grouped for scanning. Edits vs v2: em-dashes replaced
// with plain punctuation and one grammar fix ("to minimize"). Delete the v2 copy
// in faq-v2.component.ts when v2 is retired.

export interface FaqItem {
  question: string;
  answer: readonly string[];
}

export interface FaqGroup {
  title: string;
  items: readonly FaqItem[];
}

export const FAQ_GROUPS: readonly FaqGroup[] = [
  {
    title: 'Getting started',
    items: [
      {
        question: 'How do I get started with creditpath.io?',
        answer: [
          "Getting started is quick, easy, and risk free. Simply create your account, no credit card required. Once your account is confirmed, you'll have 30 days of unlimited access to submit credit requests and explore the full platform.",
          "After the 30-day trial, you'll be prompted to upgrade your account to continue submitting new requests. But don't worry: any requests submitted during your trial will remain fully accessible, even if you don't upgrade. You'll always retain visibility into your active pipeline, terms, and client activity from those first 30 days.",
          "No data is ever lost or locked out. You're simply limited from making new submission requests after the trial ends unless you choose to continue.",
        ],
      },
      {
        question: 'Are there any fees for financial advisors to use creditpath.io?',
        answer: [
          'Yes, creditpath.io uses a simple and transparent subscription model with no hidden fees. You can create an account without entering payment information and explore the full platform free for the first 30 days. During that trial, you can submit unlimited term requests and experience the complete advisor dashboard.',
          'After 30 days, you can continue using the platform by selecting a plan that fits your needs: Standard Plan at $25 per month, Pro Plan at $199 per month, or Enterprise Plan with custom pricing.',
          "Even if you choose not to upgrade after the trial, you'll still retain full visibility into any requests submitted during the 30-day period, so your data remains accessible and you never lose touch with your pipeline.",
        ],
      },
      {
        question: 'What is the process for using creditpath.io?',
        answer: [
          'The process is streamlined, advisor focused, and designed for efficiency: Submit a request through the platform, our system distributes it to vetted lenders, receive tailored terms within 24 hours, review and select the best options, share with your client, and track progress through to completion.',
          'The entire process keeps you in command without the delays, complexity, or asset interference of legacy systems.',
        ],
      },
      {
        question: 'Why should I use creditpath.io instead of other providers in the space?',
        answer: [
          'The credit space is full of noise: high cost platforms built for big firms, not real advisors. creditpath.io was built as the antidote. We designed the entire model with just two experts (one a credit solution architect trusted by other platforms, and one an active Google engineer) leveraging AI, not overhead.',
          'We operate with zero debt, no bloated salaries, and no pressure from outside investors. Our monthly base cost is just $25 because we built lean and proved it worked.',
          'Key differentiators: No asset grab games, no bloated pricing, no replacing the human, full transparency, and performance driven access.',
        ],
      },
      {
        question: 'What are the advantages of using creditpath.io for financial advisors?',
        answer: [
          'creditpath.io is built for one purpose: giving advisors the tools, access, and confidence to serve their clients without losing control.',
          "Key advantages include: No upfront cost to get started, control without compromise, exclusive terms and better pricing, speed and simplicity, white glove support, and advisor focused design whether you're a solo RIA or managing a national network.",
        ],
      },
    ],
  },
  {
    title: 'Requests and terms',
    items: [
      {
        question: 'Can I customize the credit solutions I request for clients?',
        answer: [
          "Yes. creditpath.io gives you the flexibility to tailor each credit request to your client's unique needs. You can choose from a range of products including SBLOCs, business financing, real estate credit, and luxury asset lending. Each request includes a notes section, allowing you to provide guidance or special considerations so the credit provider can deliver the most relevant options. This level of customization ensures you stay aligned with your client's goals while preserving AUM.",
        ],
      },
      {
        question: 'How quickly will I receive credit options for my clients?',
        answer: [
          'Most advisors receive credit options within 24 hours of submitting a request on creditpath.io. Responses are automatically uploaded to your dashboard, where you can review, track, and act on them in real time. This fast turnaround gives you the ability to consult with clients quickly and confidently, without the delays, guesswork, or follow-up calls of traditional credit processes.',
        ],
      },
      {
        question: 'How do I track the progress of my term requests?',
        answer: [
          'Once you submit a request through creditpath.io, communication takes place directly between you and the credit provider to stay informed on the status, required documentation, and next steps. A new dashboard is in development that will offer real time tracking, full visibility into your term request pipeline, and enhanced oversight across all client activity.',
        ],
      },
      {
        question: 'How do I share credit options with my clients?',
        answer: [
          'Once creditors return tailored options through creditpath.io, each set of terms is delivered directly to your Advisor Dashboard. From there, you can easily review the offers, accept terms to initiate the process with the creditor, and share the complete package with your client.',
          'Everything (coming soon), including terms, disclosures, and application access, is centralized in one place. Your client receives a simple link with all the information they need to review and apply, streamlining the process and eliminating back and forth.',
        ],
      },
    ],
  },
  {
    title: 'Lenders and pricing',
    items: [
      {
        question: 'Are the credit terms provided on creditpath.io competitive?',
        answer: [
          'Absolutely. All creditors on creditpath.io are required to offer terms and fees that are better than what is typically available at retail in order to remain on the platform. We work exclusively with a select network of lenders who deliver high performing solutions across real estate, business, SBLOC, and luxury asset financing.',
          'Beyond rate comparisons, our platform focuses on preserving capital by significantly reducing down payment requirements, entertaining discount points to minimize total interest paid over time.',
        ],
      },
      {
        question: 'How are the terms and fees lower than retail?',
        answer: [
          'creditpath.io only works with creditors who agree to offer better than retail pricing. This means lower fees, better terms, and more favorable structures than what your clients would find on their own.',
          "We require each provider to deliver options that help advisors preserve client capital, whether that's through reduced down payments, minimized interest paid over time, or smarter use of discount points.",
          'There are no asset movement requirements outside of purpose based SBLOCs, and we never partner with institutions that require advisors to compete for their own clients.',
        ],
      },
      {
        question: 'Why are your rates not pulled from API-based rate tables?',
        answer: [
          "creditpath.io intentionally avoids relying on static rate tables or automated API feeds because those sources often deliver outdated or misleading information. Instead, every request is reviewed by real experts to ensure accuracy, relevance, and alignment with your client's specific financial profile. This human driven approach eliminates generic estimates and ensures that the terms you present are precise, high quality, and tailored.",
        ],
      },
      {
        question: 'What are the rules of engagement for using creditpath.io?',
        answer: [
          "creditpath.io exists to protect both the integrity of advisor client relationships and the credibility of our lender network. To ensure the platform remains a trusted, high performing space for all users, we've implemented clear engagement standards.",
          'For Financial Advisors: We welcome all advisors to explore the platform with a 30-day free trial. Continued access depends on responsible use. Advisors who use the platform solely to price shop terms without engaging will be flagged and may be removed.',
          'For Creditors: All creditors must meet strict service and transparency standards. Bait and switch behavior is not tolerated.',
          'Bottom Line: This is purpose built for future minded advisors and real solution providers who want to serve clients with excellence.',
        ],
      },
    ],
  },
];
